import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { getDashboardUrl } from './runtime-config';

const AUTH_LOGIN_ENDPOINT = `${getDashboardUrl()}/api/auth/login`;
const AUTH_SET_PASSWORD_ENDPOINT = `${getDashboardUrl()}/api/auth/set-password`;
const DEFAULT_ORG_ID = 'ORG_000001';

export const AUTH_STORAGE_KEYS = {
  orgId: 'orgId',
  orgName: 'orgName',
  authToken: 'token',
  username: 'username',
  chatbotNumber: 'chatbotNumber',
  brandImageUrl: 'brandImageUrl',
  wabaId: 'wabaId',
  phoneNumberId: 'phoneNumberId',
  businessId: 'businessId',
} as const;

const LEGACY_AUTH_STORAGE_KEYS = {
  orgId: 'omnifaichat.auth.orgId',
  orgName: 'omnifaichat.auth.orgName',
  authToken: 'omnifaichat.auth.token',
  refreshToken: 'omnifaichat.auth.refreshToken',
  username: 'omnifaichat.auth.username',
  chatbotNumber: 'omnifaichat.auth.chatbotNumber',
  brandImageUrl: 'omnifaichat.auth.brandImageUrl',
  wabaId: 'omnifaichat.auth.wabaId',
  phoneNumberId: 'omnifaichat.auth.phoneNumberId',
  businessId: 'omnifaichat.auth.businessId',
} as const;

function readStorageValue(primaryKey: string, legacyKey?: string): string {
  const primary = localStorage.getItem(primaryKey)?.trim();
  if (primary) return primary;
  if (!legacyKey) return '';
  return localStorage.getItem(legacyKey)?.trim() || '';
}

export function readStoredAuthToken(): string {
  return readStorageValue(AUTH_STORAGE_KEYS.authToken, LEGACY_AUTH_STORAGE_KEYS.authToken);
}

export function readStoredOrgId(): string {
  return readStorageValue(AUTH_STORAGE_KEYS.orgId, LEGACY_AUTH_STORAGE_KEYS.orgId) || DEFAULT_ORG_ID;
}

export function readStoredOrgName(): string {
  return readStorageValue(AUTH_STORAGE_KEYS.orgName, LEGACY_AUTH_STORAGE_KEYS.orgName) || 'Your Org';
}

export function readStoredChatbotNumber(): string {
  return readStorageValue(AUTH_STORAGE_KEYS.chatbotNumber, LEGACY_AUTH_STORAGE_KEYS.chatbotNumber)
    || readStorageValue(AUTH_STORAGE_KEYS.username, LEGACY_AUTH_STORAGE_KEYS.username);
}

export function readStoredBrandImageUrl(): string {
  return readStorageValue(AUTH_STORAGE_KEYS.brandImageUrl, LEGACY_AUTH_STORAGE_KEYS.brandImageUrl);
}

export function readStoredWabaId(): string {
  return readStorageValue(AUTH_STORAGE_KEYS.wabaId, LEGACY_AUTH_STORAGE_KEYS.wabaId);
}

export function readStoredPhoneNumberId(): string {
  return readStorageValue(AUTH_STORAGE_KEYS.phoneNumberId, LEGACY_AUTH_STORAGE_KEYS.phoneNumberId);
}

export function readStoredBusinessId(): string {
  return readStorageValue(AUTH_STORAGE_KEYS.businessId, LEGACY_AUTH_STORAGE_KEYS.businessId);
}

interface AuthSession {
  orgId: string;
  orgName: string;
  authToken: string;
  username: string;
  chatbotNumber: string;
  brandImageUrl: string;
  wabaId: string;
  phoneNumberId: string;
  businessId: string;
}

type AuthApiResponse = Record<string, unknown>;

export interface SetPasswordResult {
  ok: boolean;
  tokenExpired: boolean;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  isLoggedIn = signal<boolean>(false);
  userEmail = signal<string>('');

  constructor(private readonly http: HttpClient) {
    this.restore();
  }

  async login(chatbotNumber: string, password: string): Promise<boolean> {
    const normalizedChatbotNumber = chatbotNumber.trim();
    const normalizedPassword = password.trim();
    if (!normalizedChatbotNumber || !normalizedPassword) return false;

    try {
      const response = await firstValueFrom(
        this.http.post<AuthApiResponse>(AUTH_LOGIN_ENDPOINT, {
          chatbotNumber: normalizedChatbotNumber,
          password: normalizedPassword,
        })
      );

      const payload = this.resolveAuthPayload(response);
      const token = this.readValue(payload, ['token', 'authToken', 'accessToken', 'jwtToken']);
      if (!token) return false;

      const session: AuthSession = {
        orgId: this.readValue(payload, ['orgId', 'organizationId']) || DEFAULT_ORG_ID,
        orgName: this.readValue(payload, ['orgName', 'organizationName']) || 'Your Org',
        authToken: token,
        username: normalizedChatbotNumber,
        chatbotNumber: this.readValue(payload, ['chatbotNumber', 'whatsappNumber', 'phoneNumber']) || normalizedChatbotNumber,
        brandImageUrl: this.readValue(payload, [
          'displayPictureUrl',
          'displaypicul',
          'brandImageUrl',
          'logoUrl',
          'imageUrl',
          'profileImage',
          'brandImage',
          'avatar',
          'organizationLogo',
        ]),
        wabaId: this.readValue(payload, ['wabaId', 'whatsappBusinessAccountId']),
        phoneNumberId: this.readValue(payload, ['phoneNumberId', 'whatsappPhoneNumberId']),
        businessId: this.readValue(payload, ['businessId', 'metaBusinessId']),
      };

      this.persistSession(session);
      this.isLoggedIn.set(true);
      this.userEmail.set(session.username);
      return true;
    } catch (error) {
      const status = error instanceof HttpErrorResponse ? error.status : 'unknown';
      console.error('Login failed', { status, error });
      this.logout();
      return false;
    }
  }

  logout(): void {
    this.isLoggedIn.set(false);
    this.userEmail.set('');

    localStorage.removeItem(AUTH_STORAGE_KEYS.orgId);
    localStorage.removeItem(AUTH_STORAGE_KEYS.orgName);
    localStorage.removeItem(AUTH_STORAGE_KEYS.authToken);
    localStorage.removeItem(AUTH_STORAGE_KEYS.username);
    localStorage.removeItem(AUTH_STORAGE_KEYS.chatbotNumber);
    localStorage.removeItem(AUTH_STORAGE_KEYS.brandImageUrl);
    localStorage.removeItem(AUTH_STORAGE_KEYS.wabaId);
    localStorage.removeItem(AUTH_STORAGE_KEYS.phoneNumberId);
    localStorage.removeItem(AUTH_STORAGE_KEYS.businessId);

    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEYS.orgId);
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEYS.orgName);
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEYS.authToken);
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEYS.refreshToken);
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEYS.username);
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEYS.chatbotNumber);
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEYS.brandImageUrl);
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEYS.wabaId);
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEYS.phoneNumberId);
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEYS.businessId);
  }

  requestPasswordReset(email: string): boolean {
    return !!email.trim();
  }

  getOrgId(): string {
    return readStoredOrgId();
  }

  getAuthToken(): string {
    return readStoredAuthToken();
  }

  getChatbotNumber(): string {
    return readStoredChatbotNumber();
  }

  getWabaId(): string {
    return readStoredWabaId();
  }

  getPhoneNumberId(): string {
    return readStoredPhoneNumberId();
  }

  getBusinessId(): string {
    return readStoredBusinessId();
  }

  async setPassword(orgId: string, token: string, password: string): Promise<SetPasswordResult> {
    const normalizedOrgId = orgId.trim();
    const normalizedToken = token.trim();
    const normalizedPassword = password.trim();

    if (!normalizedOrgId || !normalizedToken || !normalizedPassword) {
      return { ok: false, tokenExpired: false, message: 'Missing orgId, token, or password.' };
    }

    const endpoint = `${AUTH_SET_PASSWORD_ENDPOINT}?orgId=${encodeURIComponent(normalizedOrgId)}&token=${encodeURIComponent(normalizedToken)}`;

    try {
      const rawResponse = await firstValueFrom(
        this.http.post(endpoint, { password: normalizedPassword }, { responseType: 'text' })
      );
      const parsedResponse = this.parseMaybeJson(rawResponse);
      const message = this.extractMessage(parsedResponse) || this.extractMessage(rawResponse) || 'Password updated successfully.';
      return { ok: true, tokenExpired: false, message };
    } catch (error) {
      const defaultMessage = 'Unable to set password. Please try again.';
      if (error instanceof HttpErrorResponse) {
        const message = this.extractMessage(error.error) || defaultMessage;
        const tokenExpired = this.isTokenExpiredError(error.status, message);
        return { ok: false, tokenExpired, message };
      }
      return { ok: false, tokenExpired: false, message: defaultMessage };
    }
  }

  private restore(): void {
    const token = readStoredAuthToken();
    if (!token) return;

    this.userEmail.set(readStorageValue(AUTH_STORAGE_KEYS.username, LEGACY_AUTH_STORAGE_KEYS.username) || readStoredOrgId());
    this.isLoggedIn.set(true);
  }

  private persistSession(session: AuthSession): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.orgId, session.orgId);
    localStorage.setItem(AUTH_STORAGE_KEYS.orgName, session.orgName);
    localStorage.setItem(AUTH_STORAGE_KEYS.authToken, session.authToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.username, session.username);
    localStorage.setItem(AUTH_STORAGE_KEYS.chatbotNumber, session.chatbotNumber);
    localStorage.setItem(AUTH_STORAGE_KEYS.brandImageUrl, session.brandImageUrl);
    localStorage.setItem(AUTH_STORAGE_KEYS.wabaId, session.wabaId);
    localStorage.setItem(AUTH_STORAGE_KEYS.phoneNumberId, session.phoneNumberId);
    localStorage.setItem(AUTH_STORAGE_KEYS.businessId, session.businessId);

    // Keep legacy keys in sync for compatibility with any stale bundles still reading old keys.
    localStorage.setItem(LEGACY_AUTH_STORAGE_KEYS.orgId, session.orgId);
    localStorage.setItem(LEGACY_AUTH_STORAGE_KEYS.orgName, session.orgName);
    localStorage.setItem(LEGACY_AUTH_STORAGE_KEYS.authToken, session.authToken);
    localStorage.setItem(LEGACY_AUTH_STORAGE_KEYS.username, session.username);
    localStorage.setItem(LEGACY_AUTH_STORAGE_KEYS.chatbotNumber, session.chatbotNumber);
    localStorage.setItem(LEGACY_AUTH_STORAGE_KEYS.brandImageUrl, session.brandImageUrl);
    localStorage.setItem(LEGACY_AUTH_STORAGE_KEYS.wabaId, session.wabaId);
    localStorage.setItem(LEGACY_AUTH_STORAGE_KEYS.phoneNumberId, session.phoneNumberId);
    localStorage.setItem(LEGACY_AUTH_STORAGE_KEYS.businessId, session.businessId);
  }

  private resolveAuthPayload(response: AuthApiResponse): Record<string, unknown> {
    const data = response?.['data'];
    if (data && typeof data === 'object') return data as Record<string, unknown>;
    return response || {};
  }

  private readValue(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }

  private extractMessage(payload: unknown): string {
    if (!payload) return '';
    if (typeof payload === 'string') return payload.trim();
    if (typeof payload !== 'object') return '';

    const source = payload as Record<string, unknown>;
    const nestedData = source['data'];
    if (nestedData && typeof nestedData === 'object') {
      const nestedMessage = this.readValue(nestedData as Record<string, unknown>, ['message', 'error', 'detail', 'description']);
      if (nestedMessage) return nestedMessage;
    }

    return this.readValue(source, ['message', 'error', 'detail', 'description']);
  }

  private parseMaybeJson(payload: string): unknown {
    const normalized = payload.trim();
    if (!normalized) return '';
    try {
      return JSON.parse(normalized);
    } catch {
      return payload;
    }
  }

  private isTokenExpiredError(status: number, message: string): boolean {
    if (status === 401 || status === 403 || status === 410) return true;
    const normalized = message.toLowerCase();
    return normalized.includes('token') && (normalized.includes('expired') || normalized.includes('invalid'));
  }
}
